import { takeEvery, call, put, select, takeLatest } from 'typed-redux-saga'
import * as _ from 'lodash'
import { errorHandler, notifyMessageCreator, gridSlice } from 'Modules'
import { Deserialize } from 'cerialize'
import { RequestParams, ResponseCatalog } from 'Models'
import {
	regionsSelector,
	regionApi,
	RegionsModel,
	RegionNS,
	regionSlice,
} from 'Pages/referenceBooks'

function* getAll(action: RegionNS.GetAll) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { params, clear } = action.payload
		const { page, per_page, query, ...fields } = params
		const requestParams = new RequestParams(clear ? 1 : page, per_page, query, fields)
		if (clear) {
			yield* put(regionSlice.actions.clearAll())
		}
		const data = yield* call(regionApi.getAll, requestParams)
		data.entries = data.entries.map((item) => Deserialize(item, RegionsModel))

		if (!clear) {
			const oldData = yield* select(regionsSelector.getAll)
			data.entries = [...oldData.entries, ...data.entries]
		}
		yield* put(regionSlice.actions.setAll(data))
		yield* put(regionSlice.actions.setParams(requestParams))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getNoDivisions() {
	try {
		const res = yield* call(regionApi.getAll, new RequestParams(1, 1000))
		const entries = res.entries.map((region): RegionsModel => Deserialize(region, RegionsModel))
		const regions = Deserialize(res, ResponseCatalog)
		regions.entries = entries
		yield* put(regionSlice.actions.setAll(regions))
		const res_divisions = yield* call(
			regionApi.getAll,
			new RequestParams(1, 1000, null, { with_merchant_branches: true })
		)
		const entries_divisions = res_divisions.entries.map(
			(region): RegionsModel => Deserialize(region, RegionsModel)
		)
		yield* put(
			regionSlice.actions.setNoDivisions(_.differenceWith(entries, entries_divisions, _.isEqual))
		)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getOne(action: RegionNS.GetOne) {
	try {
		const res = yield* call(regionApi.getOne, action.payload)
		const region = Deserialize(res, RegionsModel)
		yield* put(regionSlice.actions.setOne(region))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* createOne(action: RegionNS.CreateOne) {
	try {
		yield* call(regionApi.createOne, action.payload)
		yield* put(regionSlice.actions.getAll({ params: new RequestParams(), clear: true }))
		yield* call(notifyMessageCreator, 'common.region_create.success', {
			data: [action.payload.name],
		})
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* deleteOne(action: RegionNS.DeleteOne) {
	try {
		yield* call(regionApi.deleteOne, action.payload)
		yield* put(regionSlice.actions.getAll({ params: new RequestParams(), clear: true }))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}
function* restoreOne(action: RegionNS.RestoreOne) {
	try {
		yield* call(regionApi.restoreOne, action.payload)
		yield* put(regionSlice.actions.getAll({ params: new RequestParams(), clear: true }))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* updateOne(action: RegionNS.UpdateOne) {
	try {
		const { id, region } = action.payload
		yield* call(regionApi.updateOne, id, region)
		yield* put(regionSlice.actions.getAll({ params: new RequestParams(), clear: true }))
		yield* call(notifyMessageCreator, 'common.update.success', { data: [region.name] })
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export const regionsSaga = function* saga() {
	yield* takeEvery(regionSlice.actions.createOne.type, createOne)
	yield* takeEvery(regionSlice.actions.deleteOne.type, deleteOne)
	yield* takeLatest(regionSlice.actions.getAll.type, getAll)
	yield* takeLatest(regionSlice.actions.getNoDivisions.type, getNoDivisions)
	yield* takeEvery(regionSlice.actions.getOne.type, getOne)
	yield* takeEvery(regionSlice.actions.updateOne.type, updateOne)
	yield* takeEvery(regionSlice.actions.restoreOne.type, restoreOne)
}
